import { IPromiseBasedObservable, fromPromise } from "mobx-utils";
import { RxApi, RxDto, RxSeriesDto } from "../../api-client";
import AuthStore from "../Auth/authStore";
import notificationStore from "../Notification/notificationStore";
import patientsStore from "../Patients/patientsStore"; // Import the PatientsStore

// depcrecated: all the api wrappers should be moved to rx.queries.ts, we no longer need this store for state 
// management as we use react-query for data, and PatientStore contains things like selectedRxId, etc.
export default class RxStore {
  public apiClient: RxApi;
  public authStore: AuthStore;
  public patientsStore: patientsStore;

  public constructor(apiClient: RxApi, authStore: AuthStore, patientsStore: patientsStore) {
    this.apiClient = apiClient;
    this.authStore = authStore;
    this.patientsStore = patientsStore;
  }
  public resourceRequest: IPromiseBasedObservable<any> | null = null;
  public savedSeriesForPatient: any = [];

  public setSavedSeriesForPatient = (savedSeries: RxSeriesDto[]) => {
    this.savedSeriesForPatient = savedSeries;
  };

  public getRxWithNoAppointments = async (patientId: number) => {
    const { data } = (await this.apiClient.getRxLackingAppointments(patientId)) as any;
    return data || [];
  };

  public getAppStats = async (rxIds: string[]) => {
    try {
      const { data } = await this.apiClient.getAppStats(rxIds);
      return data;
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage =
        error.response?.data?.message || "Fehler beim Abrufen der Terminstatistiken";

      notificationStore.showMessage(errorMessage, "error", error);
      return [];
    }
  };



  public deleteRx = async (rxId: string) => {
    try {
      const { data } = await this.apiClient.deleteRx(rxId);
      if (data?.deleted) {
        notificationStore.showMessage("Rezept erfolgreich gelöscht!!", "success");
        return data;
      }
      // Deletion blocked by a policy check (patient bill, attended
      // appointments, insurance bill). Surface the reason(s) to the user.
      const blockerMessage =
        data?.blockers?.map((b) => b.message).join(" ") ||
        "Rezept konnte nicht gelöscht werden!";
      notificationStore.showMessage(blockerMessage, "error");
      return null;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Rezept konnte nicht gelöscht werden!";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public restoreRx = async (rxId: string) => {
    try {
      const axios = (await import("axios")).default;
      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:3003";
      const response = await axios({
        method: "patch",
        url: `/rx/restore/${rxId}`,
        baseURL,
        headers: {
          Authorization: `Bearer ${this.authStore.authToken}`,
        },
      });
      notificationStore.showMessage("Rezept erfolgreich wiederhergestellt!", "success");
      return response.data || {};
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Rezept konnte nicht wiederhergestellt werden!";
      notificationStore.showMessage(errorMessage, "error", error);
      throw error;
    }
  };

  /**
   * MD-232: normalise every Rx form's payload onto the new prescription-line
   * model before it leaves the client.
   *
   * The Rx forms still edit a series per therapy row (their own draft shape),
   * but a series now belongs to the Rx as a whole, and doubling is a property
   * of the line (`unitsPerDay`) rather than a second shadow line. Doing this
   * once here keeps all six Rx forms (Kasse, Privat, BG, Blanko, Bundeswehr,
   * Zahnarzt) on the same contract without touching each of them.
   */
  private normaliseRxForSave = (rx: any): any => {
    const lines = (rx?.therapyRxs ?? []).map((line: any) => {
      const { rxSeries, ...rest } = line ?? {};
      return {
        ...rest,
        unitsPerDay: line?.unitsPerDay ?? (line?.isDoubled ? 2 : 1),
      };
    });
    // the series pinned on any row applies to the whole prescription
    const pinnedSeriesId =
      rx?.rxSeriesId ??
      (rx?.therapyRxs ?? []).find((line: any) => line?.rxSeries?.id)?.rxSeries?.id ??
      null;
    return { ...rx, therapyRxs: lines, rxSeriesId: pinnedSeriesId };
  };

  public saveRxs = async (patientId: number, rxDto: RxDto[]) => {
    try {
      const payload = (rxDto ?? []).map(this.normaliseRxForSave);
      const { data } = (await this.apiClient.patientSaveRxs(patientId, payload)) as any;
      // Call the updatePatientData action from PatientsStore
      // await this.patientsStore.updatePatientData(patientId);

      // MD-389: the notices live at `entry.rx.messages`, not `entry.messages`.
      // `patientSaveRxs` returns `SaveRxResponse[]` — `{ rx, therapies }` — so
      // reading one level too high always found `undefined` and every save fell
      // through to the flat success toast below, discarding the credential
      // mismatches and visitFee warnings the server had gone to the trouble of
      // building. Warning, not success: they describe something to act on.
      const messages = (data ?? [])
        .flatMap((item: any) => item?.rx?.messages ?? [])
        .map((msg: any) => (typeof msg === "string" ? msg : msg?.message))
        .filter((msg: any): msg is string => typeof msg === "string" && msg.trim() !== "");
      if (messages.length > 0) {
        notificationStore.showMessage(
          Array.from(new Set(messages)).join("\n"),
          "warning",
        );
      } else {
        // Display a default success message if no specific message is found
        notificationStore.showMessage("Rezepte erfolgreich gespeichert!", "success");
      }

      return data || [];
    } catch (error: any) {
      console.error("Error fetching rxs:", error);
      const errorMessage = error.response?.data?.message || "Fehler beim Speichern von Rezepten";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  // /**
  //  * @deprecated Use useUpdateRxProcessingStatus mutation from rx.mutations.ts instead
  //  * This method will be removed in a future version
  //  */
  // public updateprocessingStatus = async (rxId: string, status: string) => {
  //   try {
  //     const { data } = (await this.apiClient.updateProcessingStatus(rxId, status)) as any;
  //     notificationStore.showMessage(data, "success", this.patientsStore.selectedPatientId);
  //     if (this.patientsStore.selectedPatientId) {
  //       // await this.patientsStore.updatePatientData(this.patientsStore.selectedPatientId);
  //     }
  //     return data || [];
  //   } catch (error: any) {
  //     const errorMessage = error.response?.data?.message || "Fehler beim Aktualisieren des Status!";

  //     notificationStore.showMessage(errorMessage, "error", error);
  //     return null;
  //   }
  // };

  public getRxSeriesForPatient = async (patientId: number): Promise<RxSeriesDto[]> => {
    try {
      const { data } = await this.apiClient.getRxSeriesForPatient(patientId);

      return data;
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage = error.response?.data?.message || "Fehler beim Abrufen der Serien";

      notificationStore.showMessage(errorMessage, "error", error);
      return [];
    }
  };

  public getTherapyRxsSeriesForPatient = async (patientId: number) => {
    const { data } = (await this.apiClient.getTherapyRxsSeriesForPatient(patientId)) as any;
    return data || [];
  };

  public getTherapyRxsForPatient = async (patientId: number) => {
    const { data } = (await this.apiClient.getTherapyRxsForPatient(patientId)) as any;
    return data || [];
  };
  public getAllRx = async (patientId: number) => {
    const { data } = (await this.apiClient.getAllRx(patientId)) as any;
    return data || [];
  };

  public addNewSeriesForPatient = async (patientId: number, rxSeriesDto: RxSeriesDto) => {
    try {
      const { data } = (await this.apiClient.addNewSeriesToPatient(patientId, rxSeriesDto)) as any;
      notificationStore.showMessage(
        "Neue Serie erfolgreich zum Patienten hinzugefügt!!",
        "success"
      );
      return data || [];
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage =
        error.response?.data?.message || "Neue Serie konnte nicht hinzugefügt werden!!";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public deleteSeries = async (seriesId: string) => {
    try {
      const { data } = (await this.apiClient.deleteSeries(seriesId)) as any;
      notificationStore.showMessage("Serie erfolgreich gelöscht!", "success");
      return data || [];
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage =
        error.response?.data?.message || "Die Serie konnte nicht gelöscht werden!";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public updateSeries = async (seriesId: string, patientId: number, updateData: RxSeriesDto) => {
    try {
      const { data } = (await this.apiClient.updateSeries(seriesId, patientId, updateData)) as any;
      notificationStore.showMessage("Serie erfolgreich aktualisiert!", "success");
      return data || [];
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage =
        error.response?.data?.message || "Aktualisierung der Serie fehlgeschlagen!";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public updateSeriesForTherapyRx = async (therapyRxId: string, seriesId: string) => {
    try {
      const { data } = (await this.apiClient.updateSeriesForTherapyRx(
        therapyRxId,
        seriesId
      )) as any;
      notificationStore.showMessage("Serie erfolgreich aktualisiert!", "success");
      return data || [];
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage =
        error.response?.data?.message || "Aktualisierung der Serie fehlgeschlagen!";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public getRxByNumber = async (rxId: string): Promise<any | null> => {
    try {
      const { data } = (await this.apiClient.getRxByNumber(rxId)) as any;
      return data || [];
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage = error.response?.data?.message || "Rezept konnte nicht abgerufen werden!";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public getRxPriceById = async (rxId: string) => {
    try {
      const { data } = (await this.apiClient.getRxPriceById(rxId)) as any;
      return data || [];
    } catch (error: any) {
      console.error("Error fetching user:", error);
      const errorMessage = error.response?.data?.message || "Preis konnte nicht abgerufen werden!";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public updateIsTaxieren = async (rxId: string, isTaxieren: string) => {
    try {
      const { data } = (await this.apiClient.updateIsTaxieren(rxId, isTaxieren)) as any;
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Fehler beim Aktualisieren des Taxierungsstatus!";

      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  };

  public getAllTherapyCertifications = async (): Promise<any[]> => {
    try {
      const { data } = await this.apiClient.rxControllerGetAllTherapyCertifications();
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Fehler beim Abrufen der Therapie-Zertifikationen";
      notificationStore.showMessage(errorMessage, "error", error);
      return [];
    }
  };

  //   @action
  //   public postPatients = async (body:PatientDto) => {
  //     try{
  //     const { data } = (await this.apiClient.postPatients(body)) as any;
  //     return data || [];}
  //     catch (error) {
  //       this.resourceRequest = fromPromise.reject(error);

  //     }
}
